"use client";

import { Pin, PinOff } from "lucide-react";
import { useAtomValue, useSetAtom } from "jotai";
import { memo, useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  nodeDataMapAtom,
  togglePinnedFieldAtom,
  toggleDescriptionCardPinAtom,
  setPinnedFieldsAtom,
  isEditorReadOnlyAtom,
} from "@/store/editor-v2";
import { isDescriptionPinnedToCard } from "@/components/SpotflowV2/descriptionCard";

/**
 * Hover-revealed pin button for a node config field. Renders nothing if the
 * node isn't a V2 canvas node — keeps the V1 ConfigFormRenderer path unaffected.
 */
export const PinHandle = memo(function PinHandle({
  nodeId,
  fieldKey,
  className,
}: {
  nodeId: string | undefined;
  fieldKey: string;
  className?: string;
}) {
  const dataMap = useAtomValue(nodeDataMapAtom);
  const isReadOnly = useAtomValue(isEditorReadOnlyAtom);
  const togglePin = useSetAtom(togglePinnedFieldAtom);

  if (!nodeId) return null;
  if (isReadOnly) return null;
  const data = dataMap.get(nodeId);
  if (!data) return null;

  const pinned = (data.pinnedFields ?? []).includes(fieldKey);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        togglePin({ nodeId, fieldKey });
      }}
      title={pinned ? "Unpin from node card" : "Pin to node card"}
      className={cn(
        "rounded p-1 transition-opacity",
        "text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700",
        pinned
          ? "opacity-100"
          : "opacity-0 group-hover/field:opacity-100 focus:opacity-100",
        className
      )}
    >
      {pinned ? (
        <PinOff className="h-3.5 w-3.5" />
      ) : (
        <Pin className="h-3.5 w-3.5" />
      )}
    </button>
  );
});

export const DescriptionPinHandle = memo(function DescriptionPinHandle({
  nodeId,
  className,
}: {
  nodeId: string | undefined;
  className?: string;
}) {
  const dataMap = useAtomValue(nodeDataMapAtom);
  const isReadOnly = useAtomValue(isEditorReadOnlyAtom);
  const togglePin = useSetAtom(toggleDescriptionCardPinAtom);

  if (!nodeId) return null;
  if (isReadOnly) return null;
  const data = dataMap.get(nodeId);
  if (!data) return null;

  const nodeConfig = (data.nodeConfig ?? {}) as Record<string, unknown>;
  const pinned = isDescriptionPinnedToCard(nodeConfig);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        togglePin({ nodeId });
        e.currentTarget.blur();
      }}
      title={pinned ? "Unpin from node card" : "Pin to node card"}
      className={cn(
        "rounded transition-opacity",
        "text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700",
        "opacity-0 group-hover/field:opacity-100 focus-visible:opacity-100",
        className
      )}
    >
      {pinned ? (
        <PinOff className="h-3.5 w-3.5" />
      ) : (
        <Pin className="h-3.5 w-3.5" />
      )}
    </button>
  );
});

/**
 * "Pin all" toggle for a group of handles (inputs or outputs). Renders a small
 * text button that pins every key in `keys`, or unpins them all if every one
 * is already pinned.
 */
export const PinAllHandle = memo(function PinAllHandle({
  nodeId,
  keys,
  label,
}: {
  nodeId: string | undefined;
  keys: string[];
  label: string;
}) {
  const dataMap = useAtomValue(nodeDataMapAtom);
  const isReadOnly = useAtomValue(isEditorReadOnlyAtom);
  const setPinned = useSetAtom(setPinnedFieldsAtom);

  const data = nodeId ? dataMap.get(nodeId) : undefined;

  const allPinned = useMemo(() => {
    if (!data || keys.length === 0) return false;
    const set = new Set(data.pinnedFields ?? []);
    return keys.every((k) => set.has(k));
  }, [data, keys]);

  // Early returns must come *after* every hook (rules-of-hooks).
  if (isReadOnly) return null;
  if (!nodeId || !data || keys.length === 0) return null;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const current = data.pinnedFields ?? [];
        const next = allPinned
          ? current.filter((k) => !keys.includes(k))
          : Array.from(new Set([...current, ...keys]));
        setPinned({ nodeId, pinnedFields: next });
      }}
      className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-neutral-100"
      title={allPinned ? `Unpin all ${label}` : `Pin all ${label}`}
    >
      {allPinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
      {allPinned ? `Unpin all` : `Pin all`}
    </button>
  );
});
